import React, { useRef, useEffect, useState } from 'react';
import { ChevronRight, Star, Award, Briefcase, ArrowRight, Users, Globe, Building, CheckCircle, Mail, Phone, Calendar, MapPin } from 'lucide-react';
import AOS from 'aos';
import { motion } from 'framer-motion';
import 'aos/dist/aos.css';

const LuxuryHeroSection = () => {
  const [scrollY, setScrollY] = useState(0);


  const fadeInVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const staggerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section id="hero" className="relative h-[780px] flex items-center pt-12">
      {/* Background video or image */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-black opacity-60 z-10"></div>
        <img
          src="https://static.wixstatic.com/media/82fcd3_a45d22b208004b929e345048cd388ea4~mv2_d_5667_3778_s_4_2.jpg/v1/fill/w_1889,h_734,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/82fcd3_a45d22b208004b929e345048cd388ea4~mv2_d_5667_3778_s_4_2.jpg"
          alt="Hero Background"
          className="w-full h-[780px] object-cover"
        />
      </div>

      <div className="container mx-auto px-6 relative z-20 py-24 md:py-32">
        <motion.div
          className="max-w-4xl"
          initial="hidden"
          animate="visible"
          variants={staggerVariants}
        >
          <motion.div variants={fadeInVariants}>
            <span className="inline-block px-4 py-1 bg-amber-500 dark:bg-amber-500 text-white rounded-full text-sm font-medium mb-6">
              Join Our Elite Team
            </span>
          </motion.div>

          <motion.h1
            className="text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight"
            variants={fadeInVariants}
          >
            Craft Your Legacy at  <br />
            <span className="text-amber-400 dark:text-amber-400">Dev Bhoomi Group Companies</span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl"
            variants={fadeInVariants}
          >
            Join a distinguished collective of innovative minds across multiple industries.
            <span className="hidden md:inline"> Elevate your career journey with us and become part of a legacy of excellence.</span>
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6"
            variants={fadeInVariants}
          >
            <a
              href="#apply-now"
              className="px-8 py-4 bg-amber-500 dark:bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 dark:hover:bg-amber-600 transition-all duration-300 shadow-xl flex items-center justify-center"
            >
              Apply Now <ArrowRight size={18} className="ml-2" />
            </a>
            <a
              href="#our-industries"
              className="px-8 py-4 bg-transparent border-2 border-white text-white font-medium rounded-lg hover:bg-white/10 transition-all duration-300 flex items-center justify-center"
            >
              Discover Our Industries <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Enhanced WhyJoinUs component with animated cards
const WhyJoinUs = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-in-out',
    });
  }, []);

  return (
    <section id="why-join-us" className="py-20 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-black dark:to-slate-900">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 data-aos="fade-up" className="text-4xl font-serif font-bold text-gray-900 dark:text-white mb-4">Why Join <span className="text-amber-600 dark:text-amber-400">Dev Bhoomi Group Companies</span></h2>
          <p data-aos="fade-up" data-aos-delay="100" className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            At Dev Bhoomi Group Companies, talent is recognized and rewarded. Whether you are experienced or just starting out in your career,
            we provide an environment where you can thrive and grow.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          <div
            data-aos="fade-up"
            data-aos-delay="150"
            className="group bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-amber-500 transition-all duration-300 shadow-md dark:shadow-none hover:shadow-xl hover:shadow-amber-200/20 hover:-translate-y-2 relative overflow-hidden"
          >
            {/* Animated highlight glow effect on hover */}
            <div className="absolute -inset-0 bg-gradient-to-r from-amber-100 to-amber-200 opacity-0 group-hover:opacity-20 dark:from-amber-600/20 dark:to-amber-500/20 rounded-lg blur-xl group-hover:blur transition-all duration-700 group-hover:duration-1000"></div>

            {/* Animated icon background on hover */}
            <div className="bg-amber-100 dark:bg-amber-500/20 w-14 h-14 rounded-full flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-amber-100 group-hover:bg-amber-200 dark:group-hover:bg-amber-500/30 relative">
              <div className="absolute inset-0 rounded-full bg-amber-400/20 opacity-0 group-hover:opacity-100 group-hover:animate-ping"></div>
              <Star className="text-amber-600 dark:text-amber-500 w-7 h-7 group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors duration-300 relative z-10" />
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 transition-all duration-300 group-hover:text-amber-700 dark:group-hover:text-amber-300">Recognition & Growth</h3>
            <p className="text-gray-600 dark:text-gray-400 transition-all duration-300 relative">
              Your talent and hard work won't go unnoticed. We believe in recognizing excellence and providing clear paths for growth.
              <span className="h-0.5 w-0 absolute bottom-0 left-0 bg-amber-400 group-hover:w-1/3 transition-all duration-500 -mb-2"></span>
            </p>
          </div>

          <div
            data-aos="fade-up"
            data-aos-delay="300"
            className="group bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-amber-500 transition-all duration-300 shadow-md dark:shadow-none hover:shadow-xl hover:shadow-amber-200/20 hover:-translate-y-2 relative overflow-hidden"
          >
            {/* Animated highlight glow effect on hover */}
            <div className="absolute -inset-0 bg-gradient-to-r from-amber-100 to-amber-200 opacity-0 group-hover:opacity-20 dark:from-amber-600/20 dark:to-amber-500/20 rounded-lg blur-xl group-hover:blur transition-all duration-700 group-hover:duration-1000"></div>

            {/* Animated icon background on hover */}
            <div className="bg-amber-100 dark:bg-amber-500/20 w-14 h-14 rounded-full flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-amber-100 group-hover:bg-amber-200 dark:group-hover:bg-amber-500/30 relative">
              <div className="absolute inset-0 rounded-full bg-amber-400/20 opacity-0 group-hover:opacity-100 group-hover:animate-ping"></div>
              <Users className="text-amber-600 dark:text-amber-500 w-7 h-7 group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors duration-300 relative z-10" />
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 transition-all duration-300 group-hover:text-amber-700 dark:group-hover:text-amber-300">Collaborative Culture</h3>
            <p className="text-gray-600 dark:text-gray-400 transition-all duration-300 relative">
              Work alongside talented professionals in a culture that values teamwork, innovation, and mutual respect.
              <span className="h-0.5 w-0 absolute bottom-0 left-0 bg-amber-400 group-hover:w-1/3 transition-all duration-500 -mb-2"></span>
            </p>
          </div>

          <div
            data-aos="fade-up"
            data-aos-delay="450"
            className="group bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-amber-500 transition-all duration-300 shadow-md dark:shadow-none hover:shadow-xl hover:shadow-amber-200/20 hover:-translate-y-2 relative overflow-hidden"
          >
            {/* Animated highlight glow effect on hover */}
            <div className="absolute -inset-0 bg-gradient-to-r from-amber-100 to-amber-200 opacity-0 group-hover:opacity-20 dark:from-amber-600/20 dark:to-amber-500/20 rounded-lg blur-xl group-hover:blur transition-all duration-700 group-hover:duration-1000"></div>

            {/* Animated icon background on hover */}
            <div className="bg-amber-100 dark:bg-amber-500/20 w-14 h-14 rounded-full flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-amber-100 group-hover:bg-amber-200 dark:group-hover:bg-amber-500/30 relative">
              <div className="absolute inset-0 rounded-full bg-amber-400/20 opacity-0 group-hover:opacity-100 group-hover:animate-ping"></div>
              <Award className="text-amber-600 dark:text-amber-500 w-7 h-7 group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors duration-300 relative z-10" />
            </div>

            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 transition-all duration-300 group-hover:text-amber-700 dark:group-hover:text-amber-300">Industry Leadership</h3>
            <p className="text-gray-600 dark:text-gray-400 transition-all duration-300 relative">
              Be part of a group that leads in multiple industries, setting standards and driving innovation.
              <span className="h-0.5 w-0 absolute bottom-0 left-0 bg-amber-400 group-hover:w-1/3 transition-all duration-500 -mb-2"></span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Enhanced OurIndustries component with animated cards
const OurIndustries = () => {
  const industries = [
    {
      name: "Automobiles",
      icon: <Briefcase className="w-10 h-10 text-amber-600 dark:text-amber-500" />,
      description: "Join our automotive division and be a part of creating the future of transportation."
    },
    {
      name: "Steel",
      icon: <Building className="w-10 h-10 text-amber-600 dark:text-amber-500" />,
      description: "Work with cutting-edge metallurgical technology and contribute to infrastructure development."
    },
    {
      name: "Hotel",
      icon: <Building className="w-10 h-10 text-amber-600 dark:text-amber-500" />,
      description: "Create exceptional experiences in our luxury hospitality ventures."
    },
    {
      name: "Infrastructure",
      icon: <Globe className="w-10 h-10 text-amber-600 dark:text-amber-500" />,
      description: "Help build tomorrow's world with sustainable and innovative infrastructure projects."
    }
  ];

  return (
    <section id="our-industries" className="py-20 bg-gray-100 dark:bg-slate-900">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 data-aos="fade-up" className="text-4xl font-serif font-bold text-gray-900 dark:text-white mb-4">Our <span className="text-amber-600 dark:text-amber-400">Industries</span></h2>
          <p data-aos="fade-up" data-aos-delay="100" className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Dev Bhoomi Group Companies operates across multiple sectors, offering diverse career opportunities in various fields.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {industries.map((industry, index) => (
            <div
              key={index}
              data-aos="fade-up"
              data-aos-delay={150 + (index * 100)}
              className="group flex flex-col md:flex-row gap-6 p-6 bg-white/80 dark:bg-gray-800/30 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:border-amber-500 transition-all duration-500 shadow-md dark:shadow-none hover:shadow-xl hover:shadow-amber-200/20 relative overflow-hidden"
            >
              {/* Animated gradient background on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-white opacity-0 group-hover:opacity-100 dark:from-amber-900/10 dark:to-gray-800/50 transition-all duration-700 pointer-events-none"></div>

              {/* Icon container with animation */}
              <div className="flex-shrink-0 relative z-10 group-hover:scale-110 transition-transform duration-300">
                <div className="relative">
                  {/* Animated glow effect */}
                  <div className="absolute inset-0 bg-amber-200 rounded-full scale-0 group-hover:scale-110 opacity-0 group-hover:opacity-30 blur-md transition-all duration-500 dark:bg-amber-500/30"></div>

                  {/* Icon with pulsing animation */}
                  <div className="relative">
                    {React.cloneElement(industry.icon, {
                      className: `w-10 h-10 text-amber-600 dark:text-amber-500 group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors duration-300`,
                    })}
                  </div>
                </div>
              </div>

              <div className="relative z-10">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors duration-300">{industry.name}</h3>
                <p className="text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">{industry.description}</p>
                <div className="mt-4 overflow-hidden">
                  <a href="#apply-now" className="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 flex items-center text-sm font-medium group-hover:translate-x-1 transition-transform duration-300">
                    View Opportunities
                    <ArrowRight className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-300" />
                  </a>

                  {/* Animated underline */}
                  <div className="h-0.5 w-0 bg-amber-500/50 group-hover:w-full mt-1 transition-all duration-500"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ApplicationForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthMonth: '',
    address: '',
    country: '',
    city: '',
    zipCode: '',
    jobInterest: '',
    industry: '',
    whyJoin: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('https://apigulmohar.dodunsoftsolutions.com/api/apply-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          birthdayMonth: formData.birthMonth,
          address: formData.address,
          country: formData.country,
          city: formData.city,
          postalCode: formData.zipCode,
          jobLookingFor: formData.jobInterest,
          interestedIndustry: formData.industry,
          reasonToJoin: formData.whyJoin,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert('Application submitted successfully!');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          birthMonth: '',
          address: '',
          country: '',
          city: '',
          zipCode: '',
          jobInterest: '',
          industry: '',
          whyJoin: ''
        });
      } else {
        alert(`Error: ${data.message || 'Something went wrong'}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit application. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const industries = ['Automobiles', 'Steel', 'Hotel', 'Infrastructure', 'Corporate'];
  const jobs = [
    'Project Manager', 'Engineer', 'Finance Professional', 'Marketing Specialist',
    'HR Manager', 'Operations Executive', 'Hospitality Manager', 'IT Professional'
  ];

  return (
    <section id="apply-now" className="py-20 bg-gray-100 dark:bg-slate-950">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 data-aos="fade-up" className="text-4xl font-serif font-bold text-gray-900 dark:text-white mb-4">Apply <span className="text-amber-600 dark:text-amber-400">Now</span></h2>
          <p data-aos="fade-up" data-aos-delay="100" className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Take the first step towards a rewarding career at Dev Bhoomi Group Companies. Fill out the form below to apply.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <form
            onSubmit={handleSubmit}
            data-aos="fade-up"
            className="bg-white/90 dark:bg-gray-800/30 backdrop-blur-sm p-8 md:p-10 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg dark:shadow-none"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Birth Month */}
              <div>
                <label htmlFor="birthMonth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Birthday Month</label>
                <select
                  id="birthMonth"
                  name="birthMonth"
                  value={formData.birthMonth}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Select Month</option>
                  {months.map((month) => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>

              {/* Country */}
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Country/Region</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* City */}
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Zip Code */}
              <div>
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Zip / Postal Code</label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Job Interest */}
              <div>
                <label htmlFor="jobInterest" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">What Job are you looking for?</label>
                <select
                  id="jobInterest"
                  name="jobInterest"
                  value={formData.jobInterest}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Select Job</option>
                  {jobs.map((job) => (
                    <option key={job} value={job}>{job}</option>
                  ))}
                </select>
              </div>

              {/* Industry Interest */}
              <div>
                <label htmlFor="industry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Which industry are you interested in?</label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Select Industry</option>
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Address - Full Width */}
            <div className="mt-6">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Multi-line Address</label>
              <textarea
                id="address"
                name="address"
                rows="3"
                value={formData.address}
                onChange={handleChange}
                required
                className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              ></textarea>
            </div>

            {/* Why Join - Full Width */}
            <div className="mt-6">
              <label htmlFor="whyJoin" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Why do you want to work at Dev Bhoomi Group Companies?</label>
              <textarea
                id="whyJoin"
                name="whyJoin"
                rows="4"
                value={formData.whyJoin}
                onChange={handleChange}
                required
                className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md py-3 px-4 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              ></textarea>
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <button
                type="submit"
                disabled={loading}
                className={`w-full ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-600'} text-white dark:text-black font-bold py-3 px-4 rounded-md transition-all duration-300 flex items-center justify-center`}
              >
                {loading ? 'Submitting...' : 'Submit Application'}
                {!loading && <ArrowRight className="ml-2 h-5 w-5" />}
              </button>

            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const CompanyValues = () => {
  const values = [
    {
      title: "Excellence",
      description: "We pursue excellence in everything we do, setting high standards and continuously striving to exceed them."
    },
    {
      title: "Innovation",
      description: "We foster a culture of innovation, encouraging creative thinking and novel approaches to challenges."
    },
    {
      title: "Integrity",
      description: "We conduct our business with the highest ethical standards, building trust with our employees and partners."
    },
    {
      title: "Teamwork",
      description: "We believe in the power of collaboration, working together to achieve common goals and celebrate shared successes."
    }
  ];

  return (
    <section className="py-20 bg-gray-50 dark:bg-slate-900">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 data-aos="fade-up" className="text-4xl font-serif font-bold text-gray-900 dark:text-white mb-4">Our <span className="text-amber-600 dark:text-amber-400">Values</span></h2>
          <p data-aos="fade-up" data-aos-delay="100" className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            The principles that guide our work and define our culture at Dev Bhoomi Group Companies.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <div
              key={index}
              data-aos="fade-up"
              data-aos-delay={150 + (index * 100)}
              className="bg-white dark:bg-gray-800/30 backdrop-blur-sm p-6 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-amber-500 transition-all duration-300 text-center shadow-sm dark:shadow-none"
            >
              <div className="bg-amber-100 dark:bg-amber-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-amber-600 dark:text-amber-500 w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{value.title}</h3>
              <p className="text-gray-600 dark:text-gray-400">{value.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CareerPage = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-in-out',
    });
  }, []);

  return (
    <div className="bg-white dark:bg-black min-h-screen text-gray-900 dark:text-white">
      <LuxuryHeroSection />
      <WhyJoinUs />
      <OurIndustries />
      <CompanyValues />
      <ApplicationForm />
    </div>
  );
};

export default CareerPage;